import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import queryString from 'query-string';
import useSWRInfinite from 'swr/infinite';
import { parsePaginatedResponse } from 'lib/utils/parser';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { API_ROUTES } from 'lib/api-routes';
import { get } from 'lib/utils/http';
import {
  CommunityEventResponse,
  CommunityEventAttributes,
} from 'lib/models/community';

export interface ReturnType {
  events: CommunityEventAttributes[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMoreRecords: boolean;
  eventsMetaInfo: { total?: number };
  size: number;
  setSize: (size: number) => void;
  isValidating: boolean;
  mutate: () => void;
}

export const useCommunityEvents = (candidate_id: string): ReturnType => {
  const { headers } = useRecoilValue(httpHeadersState);
  const PER_PAGE_COUNT = 3;

  const constructEndpoint = () => {
    return `${API_ROUTES.STUDENT_SOCIETY_CANDIDATES}/${candidate_id}/events`;
  };

  const constructQueryString = (pageIndex: number) => {
    const params = {
      per_page: PER_PAGE_COUNT,
      with_details: true,
      page: pageIndex + 1,
    };
    return queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
    });
  };

  const getCommunityKey = (pageIndex: number) => {
    if (candidate_id) {
      return [
        `${constructEndpoint()}?${constructQueryString(pageIndex)}`,
        headers,
      ];
    }
    return null;
  };

  const {
    data: eventsResponse,
    error: eventsError,
    size,
    setSize,
    mutate,
    isValidating,
  } = useSWRInfinite<CommunityEventResponse>(getCommunityKey, get, {
    revalidateOnFocus: false,
    revalidateFirstPage: false,
  });

  const events = useMemo(() => {
    if (!eventsResponse) return [];
    const parsedResponse = parsePaginatedResponse(eventsResponse);
    return parsedResponse;
  }, [eventsResponse]);

  const hasMoreRecords = useMemo(() => {
    const firstapplicationList = Array.isArray(eventsResponse)
      ? eventsResponse[0]
      : null;
    if (firstapplicationList) {
      return (firstapplicationList?.meta?.total || 0) > size * PER_PAGE_COUNT;
    } else {
      return false;
    }
  }, [eventsResponse, size]);

  const eventsMetaInfo = useMemo(() => {
    return eventsResponse?.[0]?.meta || {};
  }, [eventsResponse]);

  const isLoading = !(eventsResponse || eventsError);

  const isLoadingMore =
    isLoading ||
    (size > 0 &&
      eventsResponse &&
      typeof eventsResponse[size - 1] === 'undefined');

  return {
    events,
    isLoading,
    isLoadingMore,
    hasMoreRecords,
    eventsMetaInfo,
    size,
    setSize,
    isValidating,
    mutate,
  };
};
